import { useState } from "react";
import { X, Package, QrCode, Clock, CheckCircle2, Truck, MapPin, User, ZoomIn, CalendarPlus, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { StorageImage } from "@/components/StorageImage";
import { formatRetailerName } from "@/lib/formatRetailer";

interface TimelineEvent {
  id: string;
  status: string;
  timestamp: string;
  description: string;
}

interface PackageDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageData?: {
    id: string;
    retailer: string;
    size: string;
    status: string;
    thumbnailUrl?: string;
    qrCodeUrl?: string;
    user: {
      fullName: string;
      email: string;
      phone?: string;
      address?: string;
    };
    manager?: {
      name: string;
    };
    proposedPickup?: {
      date: string;
      timeWindow: string;
    };
    timeline: TimelineEvent[];
    notes?: string;
    submittedAt?: string;
    canceledAt?: string;
    cancelReason?: string;
    confirmationCode?: string;
    proofOfDeliveryUrl?: string;
    proofOfPickupUrl?: string;
    returnReceiptUrl?: string;
    pickupConfirmationPhotoUrl?: string;
    returnedAt?: string;
  };
  actions?: React.ReactNode;
}

const statusConfig: Record<string, { label: string; className: string }> = {
  "pending-review": {
    label: "Pending Review",
    className: "bg-amber-100 text-amber-800 border-amber-200",
  },
  "awaiting-confirmation": {
    label: "Awaiting Confirmation",
    className: "bg-orange-100 text-orange-800 border-orange-200",
  },
  "pickup-scheduled": {
    label: "Pickup Scheduled",
    className: "bg-purple-100 text-purple-800 border-purple-200",
  },
  confirmed: {
    label: "Confirmed",
    className: "bg-emerald-100 text-emerald-800 border-emerald-200",
  },
  "picked-up": {
    label: "Picked Up",
    className: "bg-blue-100 text-blue-800 border-blue-200",
  },
  delivered: {
    label: "Delivered",
    className: "bg-green-100 text-green-800 border-green-200",
  },
  archived: {
    label: "Archived",
    className: "bg-gray-100 text-gray-600 border-gray-200",
  },
  returned: {
    label: "Returned",
    className: "bg-teal-100 text-teal-800 border-teal-200",
  },
  "user-canceled": {
    label: "Canceled by User",
    className: "bg-red-100 text-red-800 border-red-200",
  },
};

export const PackageDetailDrawer = ({
  open,
  onOpenChange,
  packageData,
  actions,
}: PackageDetailDrawerProps) => {
  const [enlargedImage, setEnlargedImage] = useState<{ url: string; bucket: string; alt: string } | null>(null);

  if (!packageData) return null;

  const status = statusConfig[packageData.status] || statusConfig["pending-review"];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle className="text-xl">Package Details</SheetTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {formatRetailerName(packageData.retailer)} • {packageData.size}
              </p>
            </div>
            <Badge variant="outline" className={cn("text-xs", status.className)}>
              {status.label}
            </Badge>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Submitted Date */}
          {packageData.submittedAt && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border">
              <CalendarPlus className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Submitted</p>
                <p className="text-sm font-medium">{packageData.submittedAt}</p>
              </div>
            </div>
          )}

          {/* Package Images */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Package Photos
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => packageData.thumbnailUrl && setEnlargedImage({ 
                  url: packageData.thumbnailUrl, 
                  bucket: "package-photos",
                  alt: "Package" 
                })}
                className="aspect-square rounded-lg bg-muted overflow-hidden border relative group cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                disabled={!packageData.thumbnailUrl}
              >
                {packageData.thumbnailUrl ? (
                  <>
                    <StorageImage
                      bucket="package-photos"
                      storedValue={packageData.thumbnailUrl}
                      alt="Package"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
              </button>
              <button
                onClick={() => packageData.qrCodeUrl && setEnlargedImage({ 
                  url: packageData.qrCodeUrl, 
                  bucket: "qr-codes",
                  alt: "QR Code" 
                })}
                className="aspect-square rounded-lg bg-muted overflow-hidden border relative group cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                disabled={!packageData.qrCodeUrl}
              >
                {packageData.qrCodeUrl ? (
                  <>
                    <StorageImage
                      bucket="qr-codes"
                      storedValue={packageData.qrCodeUrl}
                      alt="QR Code"
                      className="w-full h-full object-contain p-2"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <QrCode className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Image Enlargement Dialog */}
          <Dialog open={!!enlargedImage} onOpenChange={() => setEnlargedImage(null)}>
            <DialogContent className="max-w-2xl bg-background">
              <DialogHeader>
                <DialogTitle>{enlargedImage?.alt}</DialogTitle>
              </DialogHeader>
              <div className="flex justify-center p-4">
                {enlargedImage && (
                  <StorageImage
                    bucket={enlargedImage.bucket}
                    storedValue={enlargedImage.url}
                    alt={enlargedImage.alt}
                    className="max-w-full max-h-[60vh] object-contain rounded-lg"
                  />
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Separator />

          {/* User Info */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Customer Information
            </h3>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">{packageData.user.fullName}</p>
                  <p className="text-xs text-muted-foreground">{packageData.user.email}</p>
                </div>
              </div>
              {packageData.user.phone && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">📞</span>
                  <p className="text-sm">{packageData.user.phone}</p>
                </div>
              )}
              {packageData.user.address && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm">{packageData.user.address}</p>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Pickup Info */}
          {packageData.proposedPickup && (
            <>
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Pickup Schedule
                </h3>
                <div className="p-4 rounded-lg bg-muted/50 border">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">{packageData.proposedPickup.date}</p>
                      <p className="text-sm text-muted-foreground">
                        {packageData.proposedPickup.timeWindow}
                      </p>
                    </div>
                  </div>
                  {packageData.manager && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Assigned to: <span className="font-medium">{packageData.manager.name}</span>
                    </p>
                  )}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Confirmation & Proof Photos */}
          {(packageData.confirmationCode || packageData.proofOfDeliveryUrl || packageData.proofOfPickupUrl || packageData.returnReceiptUrl || packageData.pickupConfirmationPhotoUrl) && (
            <>
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Confirmation & Proof
                </h3>
                
                {packageData.confirmationCode && (
                  <div className="p-3 rounded-lg bg-muted/50 border">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Confirmation Code</p>
                    <p className="text-sm font-mono font-medium mt-1">{packageData.confirmationCode}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  {packageData.pickupConfirmationPhotoUrl && (
                    <button
                      onClick={() => setEnlargedImage({ url: packageData.pickupConfirmationPhotoUrl!, bucket: "pickup-confirmations", alt: "Pickup Confirmation" })}
                      className="aspect-square rounded-lg bg-muted overflow-hidden border relative group cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                    >
                      <StorageImage bucket="pickup-confirmations" storedValue={packageData.pickupConfirmationPhotoUrl} alt="Pickup Confirmation" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">Pickup</span>
                    </button>
                  )}
                  {packageData.proofOfPickupUrl && (
                    <button
                      onClick={() => setEnlargedImage({ url: packageData.proofOfPickupUrl!, bucket: "pickup-proofs", alt: "Proof of Pickup" })}
                      className="aspect-square rounded-lg bg-muted overflow-hidden border relative group cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                    >
                      <StorageImage bucket="pickup-proofs" storedValue={packageData.proofOfPickupUrl} alt="Proof of Pickup" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">Pickup Proof</span>
                    </button>
                  )}
                  {packageData.proofOfDeliveryUrl && (
                    <button
                      onClick={() => setEnlargedImage({ url: packageData.proofOfDeliveryUrl!, bucket: "delivery-confirmations", alt: "Proof of Delivery" })}
                      className="aspect-square rounded-lg bg-muted overflow-hidden border relative group cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                    >
                      <StorageImage bucket="delivery-confirmations" storedValue={packageData.proofOfDeliveryUrl} alt="Proof of Delivery" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">Delivery</span>
                    </button>
                  )}
                  {packageData.returnReceiptUrl && (
                    <button
                      onClick={() => setEnlargedImage({ url: packageData.returnReceiptUrl!, bucket: "return-receipts", alt: "Return Receipt" })}
                      className="aspect-square rounded-lg bg-muted overflow-hidden border relative group cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                    >
                      <StorageImage bucket="return-receipts" storedValue={packageData.returnReceiptUrl} alt="Return Receipt" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">Return</span>
                    </button>
                  )}
                </div>

                {packageData.returnedAt && (
                  <p className="text-xs text-muted-foreground">
                    Returned on {new Date(packageData.returnedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
              <Separator />
            </>
          )}


          <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Status Timeline
            </h3>
            <div className="space-y-4">
              {packageData.timeline.map((event, index) => (
                <div key={event.id} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center",
                      index === 0 ? "bg-primary text-primary-foreground" : "bg-muted"
                    )}>
                      {index === 0 ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-muted-foreground" />
                      )}
                    </div>
                    {index < packageData.timeline.length - 1 && (
                      <div className="w-px h-full bg-border flex-1 my-1" />
                    )}
                  </div>
                  <div className="pb-4">
                    <p className="text-sm font-medium">{event.status}</p>
                    <p className="text-xs text-muted-foreground">{event.timestamp}</p>
                    {event.description && (
                      <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cancellation Info (for user-canceled packages) */}
          {packageData.status === "user-canceled" && packageData.cancelReason && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-red-600 uppercase tracking-wider">
                  Cancellation Details
                </h3>
                <div className="bg-red-50 border border-red-200 p-3 rounded-lg space-y-2">
                  <p className="text-sm text-red-800">
                    <span className="font-medium">Reason:</span> {packageData.cancelReason}
                  </p>
                  {packageData.canceledAt && (
                    <p className="text-xs text-red-600">
                      Canceled on {new Date(packageData.canceledAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Undo History */}
          {packageData.notes && packageData.notes.includes("[UNDO") && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-amber-600 uppercase tracking-wider flex items-center gap-1.5">
                  <Undo2 className="h-3.5 w-3.5" />
                  Reversal History
                </h3>
                <div className="space-y-2">
                  {packageData.notes
                    .split("\n---\n")
                    .filter((n: string) => n.startsWith("[UNDO"))
                    .map((note: string, idx: number) => {
                      const match = note.match(/^\[UNDO - (.+?)\] (.+)$/);
                      return (
                        <div key={idx} className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 rounded-lg">
                          {match ? (
                            <>
                              <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">
                                Reversed: {match[1]}
                              </p>
                              <p className="text-sm text-amber-800 dark:text-amber-200">{match[2]}</p>
                            </>
                          ) : (
                            <p className="text-sm text-amber-800 dark:text-amber-200">{note}</p>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            </>
          )}

          {/* Notes (non-undo) */}
          {packageData.notes && (() => {
            const nonUndoNotes = packageData.notes
              .split("\n---\n")
              .filter((n: string) => !n.startsWith("[UNDO"))
              .join("\n");
            return nonUndoNotes.trim() ? (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Notes
                  </h3>
                  <p className="text-sm bg-muted/50 p-3 rounded-lg">{nonUndoNotes}</p>
                </div>
              </>
            ) : null;
          })()}

          {/* Actions */}
          {actions && (
            <>
              <Separator />
              <div className="flex flex-col gap-2">{actions}</div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
